import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PCacheable } from 'ts-cacheable';
import { DisplayInterview } from '../../beneficiaries/beneficiary-folder/interview/displayInterview';
import { Interview } from '../../beneficiaries/beneficiary-folder/interview/interview';
import { environment } from '../../environments/environment';
import { DisplayLabel } from '../models/displayLabel';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { ApiService, FileResponse } from './api.service';
import { FileService } from './file.service';

@Injectable({
  providedIn: 'root',
})
export class InterviewsService {
  private readonly interview: Subject<Interview> = new Subject<Interview>();
  public interview$: Observable<Interview> = this.interview.asObservable();

  constructor(private readonly api: ApiService, private readonly fileService: FileService) {}

  public async findByBeneficiaryId(id: number, options: PaginationOptions): Promise<Pagination<DisplayInterview>> {
    const uri: string = environment.api.interviews + '/beneficiary/' + id;
    return this.api.getPaginated<DisplayInterview>(options.buildUrl(uri));
  }

  public async findByInterviewId(idInterview: number): Promise<Interview> {
    const uri: string = environment.api.interviews + '/' + idInterview;
    const result: Interview = await this.api.get<Interview>(uri);
    return Interview.fromJson(result);
  }

  public async saveInterview(interview: Interview): Promise<void> {
    await this.api.post(environment.api.interviews, interview);
    this.interview.next(interview);
  }

  public async downloadDocument(interviewId: number, type: string): Promise<void> {
    const uri: string = environment.api.interviews + '/' + interviewId + '/document/' + type;
    const { filename, data }: FileResponse = await this.api.getFileWithName(uri);
    this.fileService.saveAs(data, filename);
  }

  public async deleteInterview(interviewId: number): Promise<void> {
    const uri: string = environment.api.interviews + '/' + interviewId;
    await this.api.delete(uri);
    this.interview.next(undefined);
  }

  public async canCreate(beneficiaryId: number): Promise<void> {
    return this.api.get<void>(environment.api.interviews + '/can-create/' + beneficiaryId);
  }

  @PCacheable()
  public async findAllAnswersProvided(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.interviews + '/answer-provided');
  }
}
